import { AttachInternals, Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/pagination?tab=designer).
 *
 * @part nav - Parte para o contêiner raiz da paginação (elemento nav), usada para estilização via ::part().
 * @part list - Parte para a lista de páginas (elemento ul), usada para estilização via ::part().
 * @part page - Parte para os itens de página numérica (elemento a), usada para estilização via ::part().
 * @part prev-button - Parte para o botão de página anterior, usada para estilização via ::part().
 * @part next-button - Parte para o botão de próxima página, usada para estilização via ::part().
 */
@Component({
  tag: 'br-pagination',
  styleUrl: 'pagination.scss',
  shadow: true,
  formAssociated: true,
})
export class Pagination {
  // Índice incremental para diferenciar instâncias em rótulos gerados
  private static instanceCounter = 0
  private readonly instanceIndex = ++Pagination.instanceCounter
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrPaginationElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Estado interno para responsividade. Verdadeiro quando viewport < sm.
   */
  @State() private isSmall = false

  private mqlSmall?: MediaQueryList
  private lastInteraction?: 'number' | 'ellipsis' | 'goTo' | 'perPage' | 'programmatic'

  /**
   * Define se a paginação usará um esquema de cores escuro.
   * Quando definido como `dark`, aplica a classe `dark-mode` ao container principal.
   */
  @Prop({ reflect: true }) readonly colorMode?: 'dark'

  /**
   * Identificador único; gerado automaticamente quando omitido.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId('br-pagination')

  /**
   * Quantidade total de páginas (mínimo 1).
   */
  @Prop({ reflect: true }) readonly total: number = 1

  /**
   * Página atual (1-indexada). Valores fora do intervalo serão ajustados.
   */
  @Prop({ reflect: true, mutable: true }) current: number = 1

  /**
   * Rótulo acessível do container `nav`.
   * Se não fornecido, será gerado automaticamente um rótulo descritivo e único
   * no momento da renderização (ex.: "Paginação: página 2 de 10").
   */
  @Prop({ reflect: true }) readonly ariaLabel: string

  /**
   * Rótulo acessível do botão de página anterior.
   */
  @Prop({ reflect: true }) readonly previousLabel: string = 'Voltar página'

  /**
   * Rótulo acessível do botão de próxima página.
   */
  @Prop({ reflect: true }) readonly nextLabel: string = 'Página seguinte'

  /**
   * Rótulo acessível do botão de reticências que abre a lista de páginas ocultas.
   */
  @Prop({ reflect: true }) readonly ellipsisLabel: string = 'Abrir ou fechar a lista de paginação'

  /**
   * Ajusta a densidade da paginação, alterando o espaçamento interno para um visual mais compacto ou mais expandido.
   */
  @Prop({ reflect: true }) readonly density: 'large' | 'medium' | 'small' = 'medium'

  /**
   * Variante de renderização do componente.
   * - `default`: paginação numérica (padrão)
   * - `contextual`: paginação contextual com seletores e informação de itens
   */
  @Prop({ reflect: true }) readonly variant: 'default' | 'contextual' = 'default'

  /**
   * Total de itens (aplicável na variante contextual).
   */
  @Prop({ reflect: true }) readonly totalItems?: number

  /**
   * Itens por página (aplicável na variante contextual).
   */
  @Prop({ reflect: true, mutable: true }) perPage: number = 10

  /**
   * Opções disponíveis de itens por página (variante contextual).
   */
  @Prop({ reflect: false }) perPageOptions: number[] = [10, 20, 30]

  /**
   * Rótulo do seletor de itens por página (variante contextual).
   */
  @Prop({ reflect: true }) readonly perPageLabel: string = 'Exibir'

  /**
   * Rótulo do seletor "ir para página" (variante contextual).
   */
  @Prop({ reflect: true }) readonly goToPageLabel: string = 'Página'

  /**
   * Sufixo textual para a informação de quantidade de itens (variante contextual).
   */
  @Prop({ reflect: true }) readonly itemsText: string = 'itens'

  /**
   * Emite quando a página muda por interação do usuário.
   */
  @Event({ bubbles: true, composed: true }) pageChange: EventEmitter<{ page: number }>

  /**
   * Emite quando o valor de itens por página é alterado (variante contextual).
   */
  @Event({ bubbles: true, composed: true }) perPageChange: EventEmitter<{ perPage: number }>

  @Watch('total')
  handleTotalChange() {
    this.current = this.clampPage(this.current)
  }

  @Watch('current')
  handleCurrentChange(newValue: number) {
    const clamped = this.clampPage(newValue)
    if (clamped !== newValue) {
      this.current = clamped
    }
  }

  componentWillLoad() {
    this.current = this.clampPage(this.current)
    this.setupMediaQuery()
  }

  connectedCallback() {
    this.setupMediaQuery()
  }

  disconnectedCallback() {
    this.cleanupMediaQuery()
  }

  private setupMediaQuery() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const query = '(max-width: 575.98px)'
    try {
      this.mqlSmall = window.matchMedia(query)
      this.isSmall = this.mqlSmall.matches
      const handler = (e: MediaQueryListEvent) => {
        this.isSmall = e.matches
      }
      if ('addEventListener' in this.mqlSmall) {
        this.mqlSmall.addEventListener('change', handler)
        ;(this.mqlSmall as MediaQueryList & { _handler?: (e: MediaQueryListEvent) => void })._handler = handler
      } else if ('addListener' in this.mqlSmall) {
        // @ts-expect-error legacy
        this.mqlSmall.addListener(handler)
        ;(this.mqlSmall as MediaQueryList & { _handler?: (e: MediaQueryListEvent) => void })._handler = handler
      }
    } catch {
      console.warn(`Media Query "${query}" is not supported in this browser.`)
      this.mqlSmall = undefined
      this.isSmall = false
    }
  }

  private cleanupMediaQuery() {
    if (this.mqlSmall === undefined) return
    const handler = (this.mqlSmall as MediaQueryList & { _handler?: (e: MediaQueryListEvent) => void })._handler
    if (handler !== undefined) {
      if ('removeEventListener' in this.mqlSmall) {
        this.mqlSmall.removeEventListener('change', handler)
      } else if ('removeListener' in this.mqlSmall) {
        // @ts-expect-error legacy
        this.mqlSmall.removeListener(handler)
      }
      delete (this.mqlSmall as MediaQueryList & { _handler?: (e: MediaQueryListEvent) => void })._handler
    }
    this.mqlSmall = undefined
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-pagination': true,
      small: this.density === 'small',
      large: this.density === 'large',
      medium: this.density === 'medium',
      'dark-mode': this.colorMode === 'dark',
    }
  }

  private clampPage(value: number): number {
    const total = this.variant === 'contextual' ? this.getTotalPages() : Math.max(1, Number(this.total) || 1)
    const clampedValue = Math.max(1, Number(value) || 1)
    return Math.min(clampedValue, total)
  }

  private readonly goTo = (page: number) => {
    const next = this.clampPage(page)
    if (next !== this.current) {
      this.current = next
      this.pageChange.emit({ page: this.current })
    }
    // Gerenciar foco após mudanças iniciadas pelo usuário
    this.queueFocusAfterNavigation()
  }

  private readonly handlePrevClick = (event: MouseEvent) => {
    event.preventDefault()
    if (this.current > 1) {
      this.goTo(this.current - 1)
    }
  }

  private readonly handleNextClick = (event: MouseEvent) => {
    event.preventDefault()
    const maxPages = this.variant === 'contextual' ? this.getTotalPages() : this.total
    if (this.current < maxPages) {
      this.goTo(this.current + 1)
    }
  }

  private readonly handlePageClickFactory = (page: number) => {
    return (event: MouseEvent) => {
      event.preventDefault()
      this.lastInteraction = 'number'
      this.goTo(page)
    }
  }

  private readonly handleEllipsisItemClickFactory = (page: number) => {
    return () => {
      this.lastInteraction = 'ellipsis'
      this.goTo(page)
    }
  }

  private getVisiblePageButtons(): HTMLElement[] {
    const root = this.el.shadowRoot
    if (root === null) return []
    return Array.from(
      root.querySelectorAll<HTMLElement>('button.page[part="page"], .pagination-ellipsis br-button[slot="trigger"]')
    )
  }

  private focusPageButtonByIndex(index: number) {
    const buttons = this.getVisiblePageButtons()
    if (buttons.length === 0) return
    const clamped = Math.max(0, Math.min(index, buttons.length - 1))
    const btn = buttons[clamped]
    // Garantir roving tabindex
    buttons.forEach((b, i) => (b.tabIndex = i === clamped ? 0 : -1))
    btn.focus()
  }

  private readonly handlePageKeyDown = (ev: KeyboardEvent) => {
    const target = ev.target as HTMLElement | null
    // Aceita tanto o botão de página quanto o botão de reticências
    if (
      !target?.matches('button.page[part="page"]') &&
      !target?.matches('.pagination-ellipsis br-button[slot="trigger"]')
    ) {
      return
    }
    const buttons = this.getVisiblePageButtons()
    const idx = buttons.indexOf(target)
    if (idx === -1) return
    switch (ev.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        ev.preventDefault()
        this.focusPageButtonByIndex(idx + 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        ev.preventDefault()
        this.focusPageButtonByIndex(idx - 1)
        break
      case 'Home':
        ev.preventDefault()
        this.focusPageButtonByIndex(0)
        break
      case 'End':
        ev.preventDefault()
        this.focusPageButtonByIndex(buttons.length - 1)
        break
      default:
        break
    }
  }

  private queueFocusAfterNavigation() {
    // Diferir para após renderização (fallback em ambientes sem RAF)
    const defer =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : (cb: FrameRequestCallback) => setTimeout(() => cb(0), 0)
    defer(() => {
      if (this.variant === 'contextual') {
        this.lastInteraction = undefined
        return
      }

      // Variant default: focar no botão da página atual ao vir de reticências
      if (this.lastInteraction === 'ellipsis' || this.lastInteraction === 'number') {
        const buttons = this.getVisiblePageButtons()
        const currentBtn = buttons.find((b) => b.getAttribute('aria-current') === 'page')
        if (currentBtn !== null && currentBtn !== undefined) {
          // Roving tabindex: somente o atual recebe 0
          buttons.forEach((b) => (b.tabIndex = b === currentBtn ? 0 : -1))
          currentBtn.focus()
        }
      }
      this.lastInteraction = undefined
    })
  }

  private getComputedNavAriaLabel(): string {
    // Respeita o valor fornecido externamente
    if (this.ariaLabel && String(this.ariaLabel).trim().length > 0) return this.ariaLabel

    // Constrói um rótulo único e descritivo baseado no estado atual
    if (this.variant === 'contextual') {
      const totalPages = this.getTotalPages()
      return `Paginação ${this.instanceIndex}: página ${this.current} de ${totalPages}`
    }
    const total = Math.max(1, Number(this.total) || 1)
    return `Paginação ${this.instanceIndex}: página ${this.current} de ${total}`
  }

  private renderPageButton(page: number) {
    return (
      <li key={page}>
        <button
          part="page"
          class={{ page: true, active: page === this.current }}
          aria-label={`Página ${page}`}
          aria-current={page === this.current ? 'page' : null}
          type="button"
          tabIndex={page === this.current ? 0 : -1}
          onClick={this.handlePageClickFactory(page)}
          onKeyDown={this.handlePageKeyDown}
        >
          {page}
        </button>
      </li>
    )
  }

  private renderEllipsis(from: number, to: number, key: string) {
    if (to < from) return null
    const items = []
    for (let p = from; p <= to; p++) {
      items.push(
        <br-item
          isButton
          role="menuitem"
          onBrDidClick={this.handleEllipsisItemClickFactory(p)}
          aria-label={`Página ${p}`}
        >
          {p}
        </br-item>
      )
    }
    return (
      <li class="pagination-ellipsis" key={`ellipsis-${key}`}>
        <br-dropdown>
          <br-button
            slot="trigger"
            shape="circle"
            type="button"
            aria-label={this.ellipsisLabel}
            tabIndex={-1}
            onKeyDown={this.handlePageKeyDown}
          >
            <br-icon icon-name="fa6-solid:ellipsis" aria-hidden="true"></br-icon>
            <span class="visually-hidden">{this.ellipsisLabel}</span>
          </br-button>
          <br-list class={{ 'has-scroll': items.length > 4 }} slot="target" role="menu">
            {items}
          </br-list>
        </br-dropdown>
      </li>
    )
  }

  private renderPages() {
    const total = Math.max(1, Number(this.total) || 1)
    const current = this.clampPage(this.current)
    const nodes = []

    // Layout compacto (xs): reduzir identificadores visíveis e usar reticências quando necessário
    if (this.isSmall) {
      if (total <= 3) {
        for (let p = 1; p <= total; p++) nodes.push(this.renderPageButton(p))
        return nodes
      }
      // Início
      if (current <= 2) {
        nodes.push(this.renderPageButton(1))
        nodes.push(this.renderPageButton(2))
        nodes.push(this.renderEllipsis(3, total - 1, 'start'))
        nodes.push(this.renderPageButton(total))
        return nodes
      }
      // Fim
      if (current >= total - 1) {
        nodes.push(this.renderPageButton(1))
        nodes.push(this.renderEllipsis(2, total - 2, 'end'))
        nodes.push(this.renderPageButton(total - 1))
        nodes.push(this.renderPageButton(total))
        return nodes
      }
      // Centro
      nodes.push(this.renderPageButton(1))
      nodes.push(this.renderEllipsis(2, current - 1, 'left'))
      nodes.push(this.renderPageButton(current))
      nodes.push(this.renderEllipsis(current + 1, total - 1, 'right'))
      nodes.push(this.renderPageButton(total))
      return nodes
    }

    // Layout padrão (sm+): comportamento atual
    if (total <= 7) {
      for (let p = 1; p <= total; p++) nodes.push(this.renderPageButton(p))
      return nodes
    }

    if (current <= 4) {
      for (let p = 1; p <= 4; p++) nodes.push(this.renderPageButton(p))
      nodes.push(this.renderEllipsis(5, total - 1, 'start'))
      nodes.push(this.renderPageButton(total))
      return nodes
    }

    if (current >= total - 3) {
      nodes.push(this.renderPageButton(1))
      nodes.push(this.renderEllipsis(2, total - 4, 'end'))
      for (let p = total - 3; p <= total; p++) nodes.push(this.renderPageButton(p))
      return nodes
    }

    nodes.push(this.renderPageButton(1))
    nodes.push(this.renderEllipsis(2, current - 2, 'left'))
    for (let p = current - 1; p <= current + 1; p++) nodes.push(this.renderPageButton(p))
    nodes.push(this.renderEllipsis(current + 2, total - 1, 'right'))
    nodes.push(this.renderPageButton(total))

    return nodes
  }

  // ====== Contextual variant helpers ======
  private getTotalPages(): number {
    const items = Math.max(0, Number(this.totalItems) || 0)
    const perPage = Math.max(1, Number(this.perPage) || 1)
    return Math.max(1, Math.ceil(items / perPage))
  }

  private getRangeInfo(): { start: number; end: number; total: number } {
    const total = Math.max(0, Number(this.totalItems) || 0)
    const perPage = Math.max(1, Number(this.perPage) || 1)
    const current = this.clampPage(this.current)
    if (total === 0) return { start: 0, end: 0, total }
    const start = (current - 1) * perPage + 1
    const end = Math.min(current * perPage, total)
    return { start, end, total }
  }

  private readonly handlePerPageChange = (event: CustomEvent<string>) => {
    const value = Number(event.detail)

    // Ignora se o valor não for um número finito ou não tiver mudado
    if (!Number.isFinite(value) || value === this.perPage) {
      return
    }

    const nextPerPage = Math.max(1, value)

    this.perPage = nextPerPage
    this.current = this.clampPage(this.current) // Ajusta a página atual
    this.perPageChange.emit({ perPage: this.perPage })

    // Gerencia o foco após a interação
    this.lastInteraction = 'perPage'
    this.queueFocusAfterNavigation()
  }

  private renderPerPageDropdown() {
    const options = this.perPageOptions.map((n) => ({
      label: String(n),
      value: String(n),
      selected: n === this.perPage,
    }))
    return (
      <br-select
        label={this.perPageLabel}
        isInline
        inputWidth="88px"
        borderless
        options={options as Array<{ label: string; value: string; selected: boolean }>}
        onValueChange={this.handlePerPageChange}
      ></br-select>
    )
  }

  private renderPerPageSelect() {
    return <div class="pagination-per-page">{this.renderPerPageDropdown()}</div>
  }

  private readonly handleGoToPageChange = (event: CustomEvent<string>) => {
    const value = Number(event.detail)
    // O fallback para this.current garante que não tentemos navegar para NaN.
    const next = this.clampPage(Number.isFinite(value) ? value : this.current)

    if (next !== this.current) {
      this.lastInteraction = 'goTo'
      this.goTo(next)
    }
  }

  private renderGoToSelect() {
    const totalPages = this.getTotalPages()
    const options = Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => ({
      label: String(p),
      value: String(p),
      selected: p === this.current,
    }))
    return (
      <div class="pagination-go-to-page d-none d-sm-flex ml-auto">
        <br-select
          label={this.goToPageLabel}
          isInline
          inputWidth="88px"
          borderless
          options={options as Array<{ label: string; value: string; selected: boolean }>}
          onValueChange={this.handleGoToPageChange}
        ></br-select>
      </div>
    )
  }

  private renderContextual() {
    const { start, end, total } = this.getRangeInfo()
    const totalPages = this.getTotalPages()
    return (
      <nav
        part="nav"
        class={this.getCssClassMap()}
        aria-label={this.getComputedNavAriaLabel()}
        tabIndex={-1}
        data-total={total}
        data-current={this.current}
        data-per-page={this.perPage}
        id={this.customId}
      >
        {this.renderPerPageSelect()}
        <span class="br-divider d-none d-sm-block mx-3"></span>
        <div class="pagination-information d-none d-sm-flex" aria-live="polite">
          <span class="current">{start}</span>
          &ndash;
          <span class="per-page">{end}</span>
          &nbsp;de&nbsp;
          <span class="total">{total}</span>
          &nbsp;{this.itemsText}
        </div>
        {this.renderGoToSelect()}
        <span class="br-divider d-none d-sm-block mx-3"></span>

        <div class="pagination-arrows ml-auto ml-sm-0">
          <br-button
            part="prev-button"
            shape="circle"
            density={this.density}
            type="button"
            data-previous-page
            aria-label={this.previousLabel}
            onClick={this.handlePrevClick}
            disabled={this.current <= 1}
          >
            <br-icon icon-name="fa6-solid:angle-left" aria-hidden="true"></br-icon>
            <span class="visually-hidden">{this.previousLabel}</span>
          </br-button>
          <br-button
            part="next-button"
            shape="circle"
            density={this.density}
            type="button"
            data-next-page
            aria-label={this.nextLabel}
            onClick={this.handleNextClick}
            disabled={this.current >= totalPages}
          >
            <br-icon icon-name="fa6-solid:angle-right" aria-hidden="true"></br-icon>
            <span class="visually-hidden">{this.nextLabel}</span>
          </br-button>
        </div>
        <span class="visually-hidden" aria-live="polite">
          Página {this.current} de {totalPages}
        </span>
      </nav>
    )
  }

  render() {
    if (this.variant === 'contextual') {
      return <Host>{this.renderContextual()}</Host>
    }

    return (
      <Host>
        <nav
          part="nav"
          class={this.getCssClassMap()}
          aria-label={this.getComputedNavAriaLabel()}
          tabIndex={-1}
          data-total={this.total}
          data-current={this.current}
          id={this.customId}
        >
          <ul part="list">
            <li>
              <br-button
                part="prev-button"
                shape="circle"
                density={this.density}
                type="button"
                data-previous-page
                aria-label={this.previousLabel}
                onClick={this.handlePrevClick}
                disabled={this.current <= 1}
              >
                <br-icon icon-name="fa6-solid:angle-left" aria-hidden="true"></br-icon>
                <span class="visually-hidden">{this.previousLabel}</span>
              </br-button>
            </li>
            {this.renderPages()}
            <li>
              <br-button
                part="next-button"
                shape="circle"
                density={this.density}
                type="button"
                data-next-page
                aria-label={this.nextLabel}
                onClick={this.handleNextClick}
                disabled={this.current >= this.total}
              >
                <br-icon icon-name="fa6-solid:angle-right" aria-hidden="true"></br-icon>
                <span class="visually-hidden">{this.nextLabel}</span>
              </br-button>
            </li>
          </ul>
          <span class="visually-hidden" aria-live="polite">
            Página {this.current} de {this.total}
          </span>
        </nav>
      </Host>
    )
  }
}
